import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { parse } from 'yaml';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { TrackerType } from '../enums/tracker';


@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(public http: HttpClient, public router: Router) {}

  handleError(error: HttpErrorResponse): Observable<never> {
    console.error('An error occurred:', error);
    // Return an observable with a user-facing error message.
    return throwError(() =>'Something bad happened; please try again later.');
  }

  loadAsset$(url: string, headers?: HttpHeaders): Observable<any> {
    return this.http.get(url, {
      observe: 'body',
      responseType: 'text',
      headers,
    }).pipe(
      catchError(this.handleError),
      map(data => parse(data))
    );
  }

  realm$(): Observable<any> {
    return this.http.get(environment.apiUrl + '/realm/')
      .pipe(catchError(this.handleError),
      );
  }

  me$(): Observable<any> {
    return this.http.get(environment.apiUrl + '/me/').pipe(
      catchError(this.handleError)
    );
  }

  detectionLabels$(): Observable<any> {
    return this.http.get(environment.apiUrl + '/arcad/detection-labels/')
      .pipe(catchError(this.handleError),
      );
  }

  organizations$(): Observable<any> {
    return this.http.get(environment.apiUrl + '/organizations/')
      .pipe(catchError(this.handleError),
      );
  }

  sites$(organizationId: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/organizations/' + organizationId + '/sites/')
      .pipe(catchError(this.handleError),
      );
  }

  users$(organizationId: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/organizations/' + organizationId + '/users/')
      .pipe(catchError(this.handleError),
      );
  }

  site$(organizationId: string, siteId: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/organizations/' + organizationId + '/sites/' + siteId + '/')
      .pipe(catchError(this.handleError),
      );
  }

  sectors$(organizationId: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/organizations/' + organizationId + '/sectors/')
      .pipe(catchError(this.handleError),
      );
  }

  batchs$(organizationId: string, siteId: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/organizations/' + organizationId + '/sites/' + siteId + '/batchs/')
      .pipe(catchError(this.handleError),
      );
  }

  resourceTmpUrl$(organizationId: string,
                  path: string,
                  filename: string | undefined): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/resource_tmp_url/',
      {
        path,
        filename
      }
    )
      .pipe(catchError(this.handleError),
      );
  }

  pointCloudDenseUrl$(organizationId: string,
                      siteId: string,
                      batchId: string,
                      filename:string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/dense_cloud/?filename='+filename)
      .pipe(catchError(this.handleError),
      );
  }

  pointCloudSparseUrl$(organizationId: string,
                       siteId: string,
                       batchId: string,
                       filename:string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/sparse_cloud/?filename='+filename)
      .pipe(catchError(this.handleError),
      );
  }

  detections$(organizationId: string,
              siteId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/arcad/detections/')
      .pipe(catchError(this.handleError),
      );
  }

  detectionComments$(organizationId: string,
                     siteId: string,
                     detectionId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/arcad/detections/' + detectionId +
      '/comments/')
      .pipe(catchError(this.handleError),
      );
  }

  addDetectionComment$(organizationId: string,
                       siteId: string,
                       detectionId: string,
                       payload: any): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/arcad/detections/' + detectionId +
      '/comments/', payload)
      .pipe(catchError(this.handleError),
      );
  }

  addDetection$(organizationId: string,
                siteId: string,
                payload: any): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/arcad/detections/', payload)
      .pipe(catchError(this.handleError),
      );
  }

  updateDetection$(organizationId: string,
                   siteId: string,
                   detectionId: string,
                   payload: any): Observable<any> {
    return this.http.put(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/arcad/detections/'+ detectionId +
      '/', payload)
      .pipe(catchError(this.handleError),
      );
  }

  addDetectionEmergency$(organizationId: string,
                         siteId: string,
                         detectionId: string,
                         payload: any): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/arcad/detections/'+ detectionId +
      '/add_emergency/', payload)
      .pipe(catchError(this.handleError),
      );
  }

  loadDetectionReferenceAttachments$(organizationId: string,
                                     siteId: string,
                                     detectionId: string,
                                     referenceBatchId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/arcad/detections/'+ detectionId +
      '/reference_attachments/?reference_batch=' + referenceBatchId)
      .pipe(catchError(this.handleError),
      );
  }

  setDetectionFavoriteAttachment$(
    organizationId: string,
    siteId: string,
    detectionId: string,
    filename: string
  ): Observable<any> {
    return this.http.put(
      `${environment.apiUrl}/organizations/${organizationId}/sites/${siteId}/arcad/detections/${detectionId}/favorite_attachment/`,
      { filename }
    );
  }

  generateDetectionsReport$(organizationId: string,
                            siteId: string,
                            formats: Array<string>,
                            ids: Array<string>): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/arcad/detections/report/', {formats, ids})
      .pipe(catchError(this.handleError),
      );
  }

  volumes$(organizationId: string,
           siteId: string,
           comparedBatchId: string,
           referenceBatchId: string,
           threshold: number): Observable<any> {
    let url = environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + comparedBatchId +
      '/perread/volumes/?threshold='+threshold+'&reference_batch='+referenceBatchId;

    return this.http.get(url)
      .pipe(catchError(this.handleError),
      );
  }

  volumeComments$(organizationId: string,
                  siteId: string,
                  batchId: string,
                  detectionId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/perread/volumes/' + detectionId +
      '/comments/')
      .pipe(catchError(this.handleError),
      );
  }

  addVolumeComment$(organizationId: string,
                       siteId: string,
                       batchId: string,
                       detectionId: string,
                       payload: any): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/perread/volumes/' + detectionId +
      '/comments/', payload)
      .pipe(catchError(this.handleError),
      );
  }

  measureDistance$(organizationId: string,
                   siteId: string,
                   batchId: string,
                   point1: Array<number>,
                   point2: Array<number>,
                   cameraPosition: Array<number>): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/measures/', {
      point1,
      point2,
      camera_position: cameraPosition,
    })
      .pipe(catchError(this.handleError),
      );
  }

  measureArea$(organizationId: string,
               siteId: string,
               batchId: string,
               polygon: Array<Array<number>>,
               cameraPosition: Array<number>): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/measures/', {
      polygon,
      camera_position: cameraPosition
    })
      .pipe(catchError(this.handleError),
      );
  }

  measureComments$(organizationId: string,
                   siteId: string,
                   batchId: string,
                   measureId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/measures/' + measureId +
      '/comments/')
      .pipe(catchError(this.handleError),
      );
  }

  addMeasureComment$(organizationId: string,
                     siteId: string,
                     batchId: string,
                     measureId: string,
                     payload: any): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/measures/' + measureId +
      '/comments/', payload)
      .pipe(catchError(this.handleError),
      );
  }

  measures$(organizationId: string,
            siteId: string,
            batchId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/measures/')
      .pipe(catchError(this.handleError),
      );
  }

  deleteMeasure$(organizationId: string,
                 siteId: string,
                 batchId: string,
                 measureId: string): Observable<any> {
    return this.http.delete(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/measures/' + measureId + '/')
      .pipe(catchError(this.handleError),
      );
  }

  updateMeasure$(organizationId: string,
                 siteId: string,
                 batchId: string,
                 measuresId: string,
                 payload: any): Observable<any> {
    return this.http.patch(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/measures/' + measuresId + '/', payload)
      .pipe(catchError(this.handleError),
      );
  }

  deleteMeasures$(organizationId: string, siteId: string, batchId: string, ids: Array<string>): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/measures/delete/', {ids})
      .pipe(catchError(this.handleError),
      );
  }

  shareMeasures$(organizationId: string, siteId: string, batchId: string, ids: Array<string>): Observable<any> {
    return this.http.patch(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/measures/share/', {ids})
      .pipe(catchError(this.handleError),
      );
  }

  shareOffMeasures$(organizationId: string, siteId: string, batchId: string, ids: Array<string>): Observable<any> {
    return this.http.patch(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/measures/shareoff/', {ids})
      .pipe(catchError(this.handleError),
      );
  }

  cuts$(organizationId: string, siteId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/')
      .pipe(catchError(this.handleError),
      );
  }

  cutProfiles$(organizationId: string, siteId: string, cutId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/' + cutId +
      '/profiles/')
      .pipe(catchError(this.handleError),
      );
  }

  cutsProfileDeviations$(organizationId: string, siteId: string, ids: Array<string>, comparedBatchId: string, referenceBatchId: string): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/profile-deviations/', {
      ids,
      'compared_batch_id': comparedBatchId,
      'reference_batch_id': referenceBatchId,
    })
      .pipe(catchError(this.handleError),
      );
  }

  cutComments$(organizationId: string, siteId: string, cutId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/' + cutId +
      '/comments/')
      .pipe(catchError(this.handleError),
      );
  }

  addCutComment$(organizationId: string, siteId: string, cutId: string, payload: any): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/' + cutId +
      '/comments/', payload)
      .pipe(catchError(this.handleError),
      );
  }

  addCut$(organizationId: string,
           siteId: string,
           point1: Array<number>,
           point2: Array<number>): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/', {point1, point2})
      .pipe(catchError(this.handleError),
      );
  }

  addCuts$(organizationId: string,
           siteId: string,
           points: Array<Array<number>>): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/add_multiple/', {points})
      .pipe(catchError(this.handleError),
      );
  }

  deleteCut$(organizationId: string, siteId: string, cutId: string): Observable<any> {
    return this.http.delete(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/' + cutId + '/')
      .pipe(catchError(this.handleError),
      );
  }

  updateCut$(organizationId: string, siteId: string, cutId: string, payload: any): Observable<any> {
    return this.http.patch(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/' + cutId + '/', payload)
      .pipe(catchError(this.handleError),
      );
  }

  exportCutsCSV$(organizationId: string,
                 siteId: string,
                 ids: Array<string>): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/csv/', {ids})
      .pipe(catchError(this.handleError),
      );
  }

  generateCutsDXF$(organizationId: string,
                   siteId: string,
                   ids: Array<string>,
                   batchId: string,
                   referenceBatchId: string | undefined): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/dxf/', {
      ids,
      batch_id:batchId,
      reference_batch_id: referenceBatchId
    }).pipe(catchError(this.handleError),
      );
  }

  generateCutsReport$(organizationId: string,
                      siteId: string,
                      formats: Array<string>,
                      ids: Array<string>,
                      batchId: string,
                      referenceBatchId: string | undefined): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/report/', {
      formats,
      ids,
      batch_id:batchId,
      reference_batch_id: referenceBatchId
    }).pipe(catchError(this.handleError),
      );
  }

  deleteCuts$(organizationId: string, siteId: string, ids: Array<string>): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/delete/', {ids})
      .pipe(catchError(this.handleError),
      );
  }

  shareCuts$(organizationId: string, siteId: string, ids: Array<string>): Observable<any> {
    return this.http.patch(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/share/', {ids})
      .pipe(catchError(this.handleError),
      );
  }

  shareOffCuts$(organizationId: string, siteId: string, ids: Array<string>): Observable<any> {
    return this.http.patch(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/cuts/shareoff/', {ids})
      .pipe(catchError(this.handleError),
      );
  }

  siteDetectionLabels$(organizationId: string,
                     siteId: string,): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/arcad/detection-labels/')
      .pipe(catchError(this.handleError),
      );
  }


  detectionEmergencyTemplate$(organizationId: string,
                              siteId: string,): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/arcad/emergency-template/')
      .pipe(catchError(this.handleError),
      );
  }

  detectionsSummary$(organizationId: string,
                     siteId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/arcad/summary/')
      .pipe(catchError(this.handleError),
      );
  }

  volumesSummary$(organizationId: string,
                  siteId: string,
                  comparedBatchId: string,
                  referenceBatchId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/perread/summary/?compared_batch='+comparedBatchId+'&reference_batch='+referenceBatchId)
      .pipe(catchError(this.handleError),
      );
  }

  generateVolumesDXF$(organizationId: string,
                      siteId: string,
                      ids: Array<string>,
                      batchId: string): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/batchs/' + batchId +
      '/perread/volumes/dxf/', {
      ids,
      batch_id:batchId
    }).pipe(catchError(this.handleError),
    );
  }

  activateSite$(organizationId: string,
                siteId: string): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/structures/activate/', {})
      .pipe(catchError(this.handleError),
      );
  }

  deactivateSite$(organizationId: string,
                  siteId: string): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/structures/deactivate/', {})
      .pipe(catchError(this.handleError),
      );
  }

  differentialTilesets$(organizationId: string, siteId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/differential-tilesets/')
      .pipe(catchError(this.handleError),
      );
  }

  addDifferentialTileset$(organizationId: string, siteId: string, batch_compared: string, batch_reference: string): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/differential-tilesets/', {batch_compared, batch_reference})
      .pipe(catchError(this.handleError),
      );
  }


  addTracker$(organizationId: string,
              siteId: string,
              point: Array<number>,
              trackerType: TrackerType): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/trackers/', {point, tracker_type: trackerType})
      .pipe(catchError(this.handleError),
      );
  }

  addTrackers$(organizationId: string,
               siteId: string,
               points: Array<Array<number>>,
               trackerType: TrackerType): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/trackers/add_multiple/', {points, tracker_type: trackerType})
      .pipe(catchError(this.handleError),
      );
  }

  trackerComments$(organizationId: string, siteId: string, trackerId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/trackers/' + trackerId +
      '/comments/')
      .pipe(catchError(this.handleError),
      );
  }

  addTrackerComment$(organizationId: string, siteId: string, trackerId: string, payload: any): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/trackers/' + trackerId +
      '/comments/', payload)
      .pipe(catchError(this.handleError),
      );
  }

  trackers$(organizationId: string, siteId: string, tracker_type: number): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/trackers/?type='+tracker_type)
      .pipe(catchError(this.handleError),
      );
  }

  deleteTracker$(organizationId: string, siteId: string, trackerId: string): Observable<any> {
    return this.http.delete(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/trackers/' + trackerId + '/')
      .pipe(catchError(this.handleError),
      );
  }

  updateTracker$(organizationId: string, siteId: string, trackerId: string, payload: any): Observable<any> {
    return this.http.patch(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/trackers/' + trackerId + '/', payload)
      .pipe(catchError(this.handleError),
      );
  }

  exportTrackersCSV$(organizationId: string,
                     siteId: string,
                     ids: Array<string>,
                     trackerType: TrackerType): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/trackers/csv/', {ids, tracker_type: trackerType})
      .pipe(catchError(this.handleError),
      );
  }

  deleteTrackers$(organizationId: string, siteId: string, ids: Array<string>): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/trackers/delete/', {ids})
      .pipe(catchError(this.handleError),
      );
  }

  shareTrackers$(organizationId: string, siteId: string, ids: Array<string>): Observable<any> {
    return this.http.patch(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/trackers/share/', {ids})
      .pipe(catchError(this.handleError),
      );
  }

  shareOffTrackers$(organizationId: string, siteId: string, ids: Array<string>): Observable<any> {
    return this.http.patch(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/trackers/shareoff/', {ids})
      .pipe(catchError(this.handleError),
      );
  }

  startPerreadPipeline$(organizationId: string,
                        siteId: string,
                        comparedBatchId: string,
                        referenceBatchId: string): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/perread/create-pipeline/', {
        'compared_batch': comparedBatchId,
        'reference_batch': referenceBatchId
    })
      .pipe(catchError(this.handleError),
      );
  }

  retrievePerreadPipeline$(organizationId: string,
                           siteId: string,
                           comparedBatchId: string,
                           referenceBatchId: string): Observable<any> {
    return this.http.get(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/perread/retrieve-pipeline/?compared_batch='+comparedBatchId+'&reference_batch='+referenceBatchId)
      .pipe(catchError(this.handleError),
      );
  }

  requestSiteExtraPresignedUrlPut$(organizationId: string, siteId: string, fileNames: Array<string>): Observable<any> {
    return this.http.post(environment.apiUrl +
      '/organizations/' + organizationId +
      '/sites/' + siteId +
      '/generate_extra_upload_presigned_urls/', fileNames)
      .pipe(catchError(this.handleError),
      );
  }

  uploadFilePut$(url: string, headers:any, body: any): Observable<any> {
    return this.http.put(url, body, { reportProgress: true, observe: 'events', headers: headers}).pipe(catchError(this.handleError));
  }

  siteUserSettings$(
    organizationId: string,
    siteId: string,
  ) {
    return this.http.get(
      environment.apiUrl +`/organizations/${organizationId}/sites/${siteId}/settings/`
    );
  }

  updateSiteUserSettings$(
    organizationId: string,
    siteId: string,
    settings: any,
  ) {
    return this.http.patch(
      environment.apiUrl +`/organizations/${organizationId}/sites/${siteId}/settings/`,
      settings,
    );
  }
}
